import React, { useState, useEffect } from 'react';
import { 
  Plus, Edit, Trash2, Save, Eye, EyeOff, X, Upload, 
  BarChart3, TrendingUp, Users, FileText, Search as SearchIcon
} from 'lucide-react';

const API_URL = 'http://localhost:8000';

const AdminKnowledgePanel = () => {
  const [articles, setArticles] = useState([]);
  const [editingArticle, setEditingArticle] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('articles'); // articles, analytics

  // Form state
  const [form, setForm] = useState({
    title: '',
    slug: '',
    description: '',
    content: '',
    category: '',
    author: 'SwissVAT Team',
    status: 'draft',
    related_articles: []
  });

  useEffect(() => {
    fetchArticles();
    fetchAnalytics();
  }, []);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/knowledge/articles`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setArticles(data.articles || []);
    } catch (error) {
      console.error('Error fetching articles:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/knowledge/analytics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleSaveArticle = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/admin/knowledge/article`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(form)
      });

      if (response.ok) {
        alert('Article saved successfully!');
        fetchArticles();
        setEditingArticle(null);
        resetForm();
      } else {
        alert('Error saving article');
      }
    } catch (error) {
      console.error('Error saving article:', error);
      alert('Error saving article');
    }
  };

  const handleDeleteArticle = async (articleId) => {
    if (!confirm('Are you sure you want to delete this article?')) return;
    
    try {
      const token = localStorage.getItem('auth_token');
      await fetch(`${API_URL}/api/admin/knowledge/article/${articleId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      fetchArticles();
    } catch (error) {
      console.error('Error deleting article:', error);
    }
  };

  const resetForm = () => {
    setForm({
      title: '',
      slug: '',
      description: '',
      content: '',
      category: '',
      author: 'SwissVAT Team',
      status: 'draft',
      related_articles: []
    });
  };

  // Auto-generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const categories = [
    'VAT Basics',
    'VAT Registration',
    'VAT Rates & Deductions',
    'Industry-Specific Guides',
    'International VAT',
    'Compliance & Reporting',
    'Special Cases'
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Knowledge Center Admin</h1>
              <p className="text-sm text-gray-600">Manage your VAT articles and documentation</p>
            </div>
            <div className="flex items-center gap-3">
              <a 
                href="/knowledge" 
                target="_blank"
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                <Eye className="w-4 h-4" />
                View Live Site
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-sm mb-6">
          <div className="border-b border-gray-200">
            <div className="flex gap-4 px-6">
              {['articles', 'analytics'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                    activeTab === tab
                      ? 'border-red-600 text-red-600'
                      : 'border-transparent text-gray-600 hover:text-gray-900'
                  }`}
                >
                  {tab === 'articles' && <FileText className="w-4 h-4 inline mr-2" />}
                  {tab === 'analytics' && <BarChart3 className="w-4 h-4 inline mr-2" />}
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>
          </div>

          <div className="p-6">
            {activeTab === 'articles' && (
              <div>
                {editingArticle !== null ? (
                  /* Article Editor */
                  <div className="space-y-6">
                    <div className="flex items-center justify-between">
                      <h2 className="text-xl font-semibold">
                        {form.id ? 'Edit Article' : 'New Article'}
                      </h2>
                      <div className="flex gap-2">
                        <button
                          onClick={() => setShowPreview(!showPreview)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
                        >
                          {showPreview ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                          Preview
                        </button>
                        <button
                          onClick={() => setEditingArticle(null)}
                          className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-6">
                      {/* Left Column - Metadata */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Article Title *
                          </label>
                          <input
                            type="text"
                            value={form.title}
                            onChange={(e) => {
                              setForm({
                                ...form,
                                title: e.target.value,
                                slug: generateSlug(e.target.value)
                              });
                            }}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="e.g., How to Register for VAT in Switzerland"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            URL Slug *
                          </label>
                          <input
                            type="text"
                            value={form.slug}
                            onChange={(e) => setForm({...form, slug: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
                            placeholder="how-to-register-for-vat"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Will be: /knowledge/{form.slug}
                          </p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Category *
                          </label>
                          <select
                            value={form.category}
                            onChange={(e) => setForm({...form, category: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            <option value="">Select category...</option>
                            {categories.map(cat => (
                              <option key={cat} value={cat}>{cat}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Description
                          </label>
                          <textarea
                            value={form.description}
                            onChange={(e) => setForm({...form, description: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                            rows="3"
                            placeholder="Brief summary of the article..."
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Author
                          </label>
                          <input
                            type="text"
                            value={form.author}
                            onChange={(e) => setForm({...form, author: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                            placeholder="SwissVAT Team"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Status
                          </label>
                          <select
                            value={form.status}
                            onChange={(e) => setForm({...form, status: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                          >
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="archived">Archived</option>
                          </select>
                        </div>
                      </div>

                      {/* Right Column - Content */}
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Article Content (HTML) *
                          </label>
                          <textarea
                            value={form.content}
                            onChange={(e) => setForm({...form, content: e.target.value})}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 font-mono text-sm"
                            rows="24"
                            placeholder="<h2>Introduction</h2>
<p>Your article content here...</p>

<h3>Key Points</h3>
<ul>
  <li>Point 1</li>
  <li>Point 2</li>
</ul>"
                          />
                        </div>

                        {showPreview && (
                          <div className="border border-gray-300 rounded-lg p-6 bg-white">
                            <h3 className="text-sm font-semibold text-gray-700 mb-4 border-b pb-2">
                              Preview
                            </h3>
                            <div 
                              className="prose prose-slate max-w-none"
                              dangerouslySetInnerHTML={{ __html: form.content }}
                            />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Save Buttons */}
                    <div className="flex justify-between items-center pt-6 border-t">
                      <div className="text-sm text-gray-600">
                        <strong>Tip:</strong> You can use HTML for formatting. Preview to check appearance.
                      </div>
                      <div className="flex gap-3">
                        <button
                          onClick={() => {
                            setForm({...form, status: 'draft'});
                            setTimeout(handleSaveArticle, 100);
                          }}
                          className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                        >
                          Save as Draft
                        </button>
                        <button
                          onClick={() => {
                            setForm({...form, status: 'published'});
                            setTimeout(handleSaveArticle, 100);
                          }}
                          className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                        >
                          <Save className="w-4 h-4" />
                          Publish Article
                        </button>
                      </div>
                    </div>
                  </div>
                ) : (
                  /* Articles List */
                  <div>
                    <div className="flex items-center justify-between mb-6">
                      <div>
                        <h2 className="text-xl font-semibold text-gray-900">All Articles</h2>
                        <p className="text-sm text-gray-600">Manage your knowledge base content</p>
                      </div>
                      <button
                        onClick={() => {
                          resetForm();
                          setEditingArticle(0);
                        }}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2"
                      >
                        <Plus className="w-4 h-4" />
                        New Article
                      </button>
                    </div>

                    {/* Articles Table */}
                    <div className="space-y-3">
                      {articles.length === 0 ? (
                        <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-300">
                          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <h3 className="text-lg font-medium text-gray-900 mb-2">
                            No articles yet
                          </h3>
                          <p className="text-gray-600 mb-6">
                            Get started by creating your first article
                          </p>
                          <button
                            onClick={() => {
                              resetForm();
                              setEditingArticle(0);
                            }}
                            className="px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700"
                          >
                            Create First Article
                          </button>
                        </div>
                      ) : (
                        articles.map(article => (
                          <div
                            key={article.id}
                            className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg hover:border-red-300 transition-colors"
                          >
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-1">
                                <span className={`px-2 py-1 text-xs rounded ${
                                  article.status === 'published' 
                                    ? 'bg-green-100 text-green-700'
                                    : article.status === 'draft'
                                    ? 'bg-yellow-100 text-yellow-700'
                                    : 'bg-gray-100 text-gray-700'
                                }`}>
                                  {article.status}
                                </span>
                                {article.category && (
                                  <span className="text-xs text-gray-500">
                                    {article.category}
                                  </span>
                                )}
                              </div>
                              <h3 className="font-semibold text-gray-900 mb-1">
                                {article.title}
                              </h3>
                              {article.description && (
                                <p className="text-sm text-gray-600 mb-2">
                                  {article.description}
                                </p>
                              )}
                              <div className="flex items-center gap-4 text-xs text-gray-500">
                                <span>By {article.author || 'Unknown'}</span>
                                {article.views !== undefined && (
                                  <span>{article.views} views</span>
                                )}
                                {article.updated_at && (
                                  <span>Updated {new Date(article.updated_at).toLocaleDateString()}</span>
                                )}
                              </div>
                            </div>
                            <div className="flex gap-2 ml-4">
                              <a
                                href={`/knowledge?article=${article.slug}`}
                                target="_blank"
                                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                title="View article"
                              >
                                <Eye className="w-4 h-4" />
                              </a>
                              <button
                                onClick={() => {
                                  setForm(article);
                                  setEditingArticle(article.id);
                                }}
                                className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                                title="Edit article"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteArticle(article.id)}
                                className="p-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50"
                                title="Delete article"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'analytics' && analytics && (
              <div className="space-y-6">
                {/* Stats Cards */}
                <div className="grid grid-cols-3 gap-6">
                  <div className="bg-blue-50 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-blue-600 font-medium">
                          Total Articles
                        </p>
                        <p className="text-3xl font-bold text-blue-900 mt-2">
                          {analytics.total_articles || 0}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">
                          {analytics.published_articles || 0} published
                        </p>
                      </div>
                      <FileText className="w-12 h-12 text-blue-300" />
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-green-600 font-medium">
                          Total Views
                        </p>
                        <p className="text-3xl font-bold text-green-900 mt-2">
                          {(analytics.total_views || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-green-600 mt-1">
                          Last 30 days
                        </p>
                      </div>
                      <TrendingUp className="w-12 h-12 text-green-300" />
                    </div>
                  </div>

                  <div className="bg-purple-50 rounded-lg p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-purple-600 font-medium">
                          Searches
                        </p>
                        <p className="text-3xl font-bold text-purple-900 mt-2">
                          {(analytics.total_searches || 0).toLocaleString()}
                        </p>
                        <p className="text-xs text-purple-600 mt-1">
                          Last 7 days
                        </p>
                      </div>
                      <SearchIcon className="w-12 h-12 text-purple-300" />
                    </div>
                  </div>
                </div>

                {/* Top Articles */}
                {analytics.top_articles && analytics.top_articles.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Most Viewed Articles
                    </h3>
                    <div className="space-y-3">
                      {analytics.top_articles.map((article, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-3 flex-1">
                            <span className="text-2xl font-bold text-gray-300">
                              {idx + 1}
                            </span>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">
                                {article.title}
                              </p>
                              <p className="text-sm text-gray-500">
                                /knowledge/{article.slug}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 text-gray-600">
                            <Eye className="w-4 h-4" />
                            <span className="font-semibold">
                              {article.views}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Top Searches */}
                {analytics.top_searches && analytics.top_searches.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold mb-4">
                      Popular Search Terms
                    </h3>
                    <div className="grid grid-cols-2 gap-3">
                      {analytics.top_searches.map((search, idx) => (
                        <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <span className="text-gray-900">{search.query}</span>
                          <span className="text-sm text-gray-600 font-medium">
                            {search.count}
                          </span>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-500 mt-4">
                      💡 Tip: Create articles for popular search terms to improve coverage
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminKnowledgePanel;
